/**
 * Library API Service
 * 
 * User's Manga Collection (Regal)
 * 
 * Mangas:
 * GET    /library/mangas                    - Alle Mangas der Library (cursor-paginiert)
 * GET    /library/mangas/search?q=          - Suche in Library (optional)
 * GET    /library/mangas/{manga_id}         - Manga Details
 * DELETE /library/mangas/{manga_id}         - Manga aus Library entfernen
 * 
 * Bands:
 * GET    /library/mangas/{manga_id}/bands   - Bände eines Mangas (cursor-paginiert)
 * PATCH  /library/bands/{band_id}           - reading_status aktualisieren
 * DELETE /library/bands/{band_id}           - Band aus Library entfernen
 * 
 * Progress:
 * GET    /library/mangas/{manga_id}/progress - Fortschritt eines Mangas
 */

import { get, post, put, patch, del, buildCursorParams } from '../client';

// ============================================================================
// MANGAS
// ============================================================================

/**
 * Alle Mangas der User-Library laden (Minimaldaten fürs Regal)
 * @param {object} options - { cursor?, limit?, sort?, filter? }
 * @returns {Promise<{success: boolean, data?: {items: MangaListItem[], next_cursor: string|null}, error?: object}>}
 *
 * Response Item:
 * {
 *   manga_id: number,
 *   title: string,
 *   cover_media_key: string
 * }
 *
 * sort:   TITLE_ASC | TITLE_DESC | RECENTLY_ADDED | NEXT_RELEASE |
 *         COLLECTION_PROGRESS | READING_PROGRESS | MISSING_BANDS
 * filter: ALL | COMPLETE | INCOMPLETE | UNREAD | NEW_RELEASES |
 *         MISSING_BANDS | WISHLIST
 */
export const getMangas = async (options = {}) => {
  const params = buildCursorParams(options);
  if (options.sort) params.sort = options.sort;
  if (options.filter) params.filter = options.filter;
  return get('/library/mangas', params);
};

/**
 * Alle Mangas laden (mit automatischem Cursor-Following)
 * @param {object|number} options - { sort?, filter?, maxLimit? } oder (legacy) maxLimit als Zahl
 * @returns {Promise<{success: boolean, items?: MangaListItem[], error?: object}>}
 */
export const getAllMangas = async (options = {}) => {
  const { sort, filter, maxLimit = 1000 } = typeof options === 'number' ? { maxLimit: options } : options;
  const allItems = [];
  let cursor = null;

  while (allItems.length < maxLimit) {
    const result = await getMangas({ cursor, limit: 200, sort, filter });

    if (!result.success) {
      return result;
    }

    const items = result.data?.items || [];
    allItems.push(...items);

    cursor = result.data?.next_cursor;
    if (!cursor || items.length === 0) {
      break;
    }
  }

  return { success: true, items: allItems };
};

// ============================================================================
// SORT / FILTER OPTIONS (für UI-Dropdowns)
// ============================================================================

export const SORT_OPTIONS = [
  { value: '', label: 'Standard' },
  { value: 'TITLE_ASC', label: 'Titel (A–Z)' },
  { value: 'TITLE_DESC', label: 'Titel (Z–A)' },
  { value: 'RECENTLY_ADDED', label: 'Zuletzt hinzugefügt' },
  { value: 'NEXT_RELEASE', label: 'Nächste Veröffentlichung' },
  { value: 'COLLECTION_PROGRESS', label: 'Sammlungsfortschritt' },
  { value: 'READING_PROGRESS', label: 'Lesefortschritt' },
  { value: 'MISSING_BANDS', label: 'Fehlende Bände' },
];

export const FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'COMPLETE', label: 'Vollständig' },
  { value: 'INCOMPLETE', label: 'Unvollständig' },
  { value: 'UNREAD', label: 'Ungelesen' },
  { value: 'NEW_RELEASES', label: 'Neue Veröffentlichungen' },
  { value: 'MISSING_BANDS', label: 'Fehlende Bände' },
  { value: 'WISHLIST', label: 'Auf Wunschliste' },
];

/**
 * Suche in User-Library
 * @param {string} query - Suchbegriff
 * @param {number} limit - Max Ergebnisse (default: 200)
 * @returns {Promise<{success: boolean, data?: MangaListItem[], error?: object}>}
 */
export const searchMangas = async (query, limit = 200) => {
  return get('/library/mangas/search', { q: query, limit });
};

/**
 * Manga-Details laden
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: MangaDetail, error?: object}>}
 * 
 * Response Data:
 * {
 *   manga_id: number,
 *   title: string,
 *   description: string,
 *   status: string (RUNNING, COMPLETED, etc.),
 *   publisher: string,
 *   genres: string[],
 *   band_count: number,
 *   cover_media_key: string,
 *   average_score: number,
 *   mean_score: number,
 *   rankings: number
 * }
 */
export const getMangaById = async (mangaId) => {
  return get(`/library/mangas/${mangaId}`);
};

/**
 * Manga-Serie zur Library hinzufügen (OHNE ISBN, §9) – legt automatisch alle
 * bekannten Bände der Serie als Platzhalter an, die im Hintergrund mit echten
 * Daten (Autor/Verlag/Cover/Erscheinungsdatum) angereichert werden.
 * @param {number} mangaId - Manga ID (aus der globalen Suche)
 * @returns {Promise<{success: boolean, data?: BandAddResult, error?: object}>}
 *
 * Errors: 404 NOT_FOUND (Manga nicht in DB), 409 ALREADY_OWNED
 */
export const addSeries = async (mangaId) => {
  return post(`/library/mangas/${mangaId}`);
};

/**
 * Manga aus Library entfernen (löscht alle Bände)
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 404 NOT_FOUND
 */
export const removeManga = async (mangaId) => {
  return del(`/library/mangas/${mangaId}`);
};

// ============================================================================
// BANDS
// ============================================================================

/**
 * Alle globalen Bände eines Mangas laden (unabhängig vom User-Besitz)
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: {items: GlobalBandItem[]}, error?: object}>}
 *
 * Response Item:
 * {
 *   band_id: number,
 *   band_number: number,
 *   band_title: string,
 *   cover_media_key: string
 * }
 *
 * GET /mangas/{manga_id}/bands
 */
export const getGlobalBands = async (mangaId) => {
  return get(`/mangas/${mangaId}/bands`);
};

/**
 * Bände eines Mangas in der Library laden
 * @param {number} mangaId - Manga ID
 * @param {object} options - { cursor?, limit? }
 * @returns {Promise<{success: boolean, data?: {items: BandListItem[], next_cursor: string|null}, error?: object}>}
 * 
 * Response Item:
 * {
 *   band_id: number,
 *   band_number: number,
 *   band_title: string,
 *   cover_media_key: string,
 *   reading_status: string (READ, UNREAD)
 * }
 */
export const getBands = async (mangaId, options = {}) => {
  const params = buildCursorParams({ ...options, limit: options.limit || 300 });
  return get(`/library/mangas/${mangaId}/bands`, params);
};

/**
 * Alle Bände eines Mangas laden (owned + unowned, mit band_id für alle)
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, items?: BandListItem[], error?: object}>}
 *
 * Response Item (neu):
 * {
 *   band_id: number,
 *   band_number: number,
 *   band_title: string,
 *   cover_media_key: string,
 *   owned: boolean,
 *   reading_status?: string  // nur wenn owned=true
 * }
 */
export const getAllBands = async (mangaId) => {
  const result = await getBands(mangaId, { limit: 500 });
  if (!result.success) return result;
  return { success: true, items: result.data?.items || [] };
};

/**
 * Reading Status eines Bandes aktualisieren
 * @param {number} bandId - Band ID
 * @param {string} readingStatus - 'READ' oder 'UNREAD'
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 400 VALIDATION_ERROR, 404 NOT_FOUND
 */
export const updateBandStatus = async (bandId, readingStatus) => {
  return patch(`/library/bands/${bandId}`, { reading_status: readingStatus });
};

/**
 * Band aus Library entfernen
 * @param {number} bandId - Band ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 404 NOT_FOUND
 */
export const removeBand = async (bandId) => {
  return del(`/library/bands/${bandId}`);
};

// ============================================================================
// PROGRESS
// ============================================================================

/**
 * Fortschritt eines Mangas laden
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: MangaProgress, error?: object}>}
 * 
 * Response Data:
 * {
 *   owned_bands: number,
 *   total_bands: number,
 *   percent: number
 * }
 */
export const getMangaProgress = async (mangaId) => {
  return get(`/library/mangas/${mangaId}/progress`);
};

/**
 * Band per Bandnummer zur Library hinzufügen (kein ISBN nötig)
 * @param {number} mangaId     - Manga ID (Manga muss bereits in Library sein)
 * @param {number} bandNumber  - Band-Nummer (z.B. 2)
 * @returns {Promise<{success: boolean, data?: BandAddResult, error?: object}>}
 *
 * Response Data:
 * {
 *   band_id: number,
 *   band_number: number,
 *   band_title: string,
 *   cover_media_key: string,
 *   reading_status: string
 * }
 *
 * Errors: 404 NOT_FOUND, 409 ALREADY_OWNED
 */
export const addBandByNumber = async (mangaId, bandNumber) => {
  return post(`/library/mangas/${mangaId}/bands/${bandNumber}`);
};

// ============================================================================
// NOTIFICATION SETTINGS (per Serie)
// ============================================================================

/**
 * Effektive Benachrichtigungseinstellungen für eine Serie laden
 * (Override falls vorhanden, sonst globale Defaults aus /users/me/settings)
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: MangaNotificationSettings, error?: object}>}
 *
 * Response Data:
 * {
 *   new_release_enabled: boolean,
 *   popup_enabled: boolean,
 *   email_enabled: boolean,
 *   auto_add_to_wishlist: boolean
 * }
 */
export const getMangaNotificationSettings = async (mangaId) => {
  return get(`/library/mangas/${mangaId}/notification-settings`);
};

/**
 * Pro-Serie-Override für Benachrichtigungen setzen (kein Partial! Alle 4 Felder Pflicht)
 * @param {number} mangaId - Manga ID
 * @param {MangaNotificationSettings} settings - Vollständiges Settings-Objekt
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400 VALIDATION_ERROR
 */
export const updateMangaNotificationSettings = async (mangaId, settings) => {
  return put(`/library/mangas/${mangaId}/notification-settings`, settings);
};

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} MangaNotificationSettings
 * @property {boolean} new_release_enabled
 * @property {boolean} popup_enabled
 * @property {boolean} email_enabled
 * @property {boolean} auto_add_to_wishlist
 */

/**
 * @typedef {object} MangaListItem
 * @property {number} manga_id
 * @property {string} title
 * @property {string} cover_media_key
 */

/**
 * @typedef {object} MangaDetail
 * @property {number} manga_id
 * @property {string} title
 * @property {string} description
 * @property {string} status
 * @property {string} publisher
 * @property {string[]} genres
 * @property {number} band_count
 * @property {string} cover_media_key
 * @property {number} average_score
 * @property {number} mean_score
 * @property {number} rankings
 */

/**
 * @typedef {object} BandListItem
 * @property {number} band_id
 * @property {number} band_number
 * @property {string} band_title
 * @property {string} cover_media_key
 * @property {string} reading_status
 */

/**
 * @typedef {object} MangaProgress
 * @property {number} owned_bands
 * @property {number} total_bands
 * @property {number} percent
 */
